import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { colors, spacing, radius, shadow, zIndex, typography, transitions } from '../../theme/tokens';

/**
 * CommandPalette — paleta de comandos con Cmd+K / Ctrl+K.
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {Array<{id, title, subtitle?, group?, icon?, action}>} items
 *        Cada item tiene un `action()` que se invoca al seleccionar.
 * @param {string} placeholder — default "Buscar comandos..."
 */
const CommandPalette = ({ open, onClose, items = [], placeholder = 'Buscar comandos...' }) => {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const normalizedQuery = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!normalizedQuery) return items;
    return items.filter((it) => {
      const hay = `${it.title || ''} ${it.subtitle || ''} ${it.group || ''}`.toLowerCase();
      return hay.includes(normalizedQuery);
    });
  }, [items, normalizedQuery]);

  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((it) => {
      const g = it.group || 'Resultados';
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(it);
    });
    return [...map.entries()];
  }, [filtered]);

  useEffect(() => {
    if (activeIndex >= filtered.length) setActiveIndex(Math.max(0, filtered.length - 1));
  }, [filtered.length, activeIndex]);

  const handleKey = useCallback((e) => {
    if (!open) return;
    if (e.key === 'Escape') { e.preventDefault(); onClose?.(); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = filtered[activeIndex];
      if (item) { item.action?.(); onClose?.(); }
    }
  }, [open, filtered, activeIndex, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    document.addEventListener('keydown', handleKey, true);
    return () => document.removeEventListener('keydown', handleKey, true);
  }, [open, handleKey]);

  // Scroll active into view
  useEffect(() => {
    if (!open || !listRef.current) return;
    const active = listRef.current.querySelector('[data-active="true"]');
    if (active && typeof active.scrollIntoView === 'function') active.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  if (!open) return null;

  const content = (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(17, 24, 39, 0.4)',
        zIndex: zIndex.modal,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: spacing[7],
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Paleta de comandos"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: colors.surface,
          borderRadius: radius.md,
          boxShadow: shadow.lg,
          width: '100%',
          maxWidth: 560,
          overflow: 'hidden',
          animation: `boveda-palette-in ${transitions.base} both`,
        }}
      >
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
          placeholder={placeholder}
          aria-label="Buscar"
          style={{
            width: '100%',
            padding: `${spacing[4]}px ${spacing[5]}px`,
            border: 'none',
            borderBottom: `1px solid ${colors.border}`,
            fontSize: typography.fontSize.md,
            color: colors.text,
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        <div ref={listRef} style={{ maxHeight: 420, overflowY: 'auto' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: spacing[5], color: colors.textMuted, textAlign: 'center', fontSize: typography.fontSize.base }}>
              Sin resultados
            </div>
          ) : grouped.map(([groupName, groupItems]) => (
            <div key={groupName}>
              <div style={{
                padding: `${spacing[2]}px ${spacing[5]}px`,
                fontSize: typography.fontSize.xs,
                color: colors.textSubtle,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                backgroundColor: colors.gray50,
              }}>
                {groupName}
              </div>
              {groupItems.map((item) => {
                const flatIndex = filtered.indexOf(item);
                const active = flatIndex === activeIndex;
                return (
                  <button
                    key={item.id}
                    type="button"
                    data-active={active}
                    onClick={() => { item.action?.(); onClose?.(); }}
                    onMouseEnter={() => setActiveIndex(flatIndex)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: spacing[3],
                      width: '100%',
                      padding: `${spacing[3]}px ${spacing[5]}px`,
                      background: active ? colors.primaryBg : 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      color: colors.text,
                      fontSize: typography.fontSize.base,
                    }}
                  >
                    {item.icon && <span style={{ color: colors.primary, fontSize: typography.fontSize.lg, display: 'inline-flex' }}>{item.icon}</span>}
                    <span style={{ flex: 1 }}>
                      <div style={{ fontWeight: typography.fontWeight.medium }}>{item.title}</div>
                      {item.subtitle && (
                        <div style={{ fontSize: typography.fontSize.xs, color: colors.textMuted, marginTop: 2 }}>
                          {item.subtitle}
                        </div>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <div style={{
          padding: `${spacing[2]}px ${spacing[5]}px`,
          borderTop: `1px solid ${colors.border}`,
          fontSize: typography.fontSize.xs,
          color: colors.textMuted,
          display: 'flex',
          gap: spacing[4],
          backgroundColor: colors.gray50,
        }}>
          <span><kbd style={kbdStyle}>↑</kbd> <kbd style={kbdStyle}>↓</kbd> navegar</span>
          <span><kbd style={kbdStyle}>↵</kbd> ejecutar</span>
          <span><kbd style={kbdStyle}>esc</kbd> cerrar</span>
        </div>
        <style>{`
          @keyframes boveda-palette-in {
            from { opacity: 0; transform: translateY(-8px); }
            to   { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

const kbdStyle = {
  padding: '1px 5px',
  background: '#fff',
  border: '1px solid #D1D5DB',
  borderRadius: 3,
  fontFamily: "'SF Mono', Menlo, monospace",
  fontSize: 11,
};

export default CommandPalette;
